import React, { ReactNode } from 'react';
import { SnackbarUtilsConfigurator } from './components/snackbar';
import ApolloProviderWrapper from './context/apollo';
import CustomSnackbarProvider from './context/custom-snackbar/provider';
import { GraphqlProvider } from './context/graphql';
import InstallationProvider from './context/installation';
import NativeProvider from './context/native';
import NativeFunctionsProvider from './context/native-functions';
import { ExalusContextProvider } from './integrations/exalus/context';
import { ExalusDevicesContextProvider } from './integrations/exalus/context/devices';
import { ExalusServicesContextProvider } from './integrations/exalus/context/services';
import { ExalusStorageContextProvider } from './integrations/exalus/context/storage';
import { ExalusCreateActionContextProvider } from './integrations/exalus/modules/action-create/context';
import DevicesAndChannelsProvider from './integrations/lavva/context/devices-and-channels';
import { LavvaCreateActionContextProvider } from './integrations/lavva/modules/action-create/context';
import { CreateInstallationProvider } from './modules/installation-create/context';
import { ShareInstallationProvider } from './modules/installation-share/context';
import { OverApp } from './modules/over-app/over-app';
import { OverAppLocal } from './modules/over-app/over-app-local';
import './styles/main.scss';
import { isLocalApp } from './utils/helpers/local-app';

type ProvidersProps = {
  children: ReactNode;
};

const ProvidersLocal: React.FC<ProvidersProps> = ({ children }) => (
  <CustomSnackbarProvider>
    <SnackbarUtilsConfigurator />
    <ApolloProviderWrapper>
      <ExalusServicesContextProvider>
        <ExalusStorageContextProvider>
          <ExalusDevicesContextProvider>
            <ExalusContextProvider>
              <InstallationProvider>
                <NativeFunctionsProvider>
                  <NativeProvider>
                    <CreateInstallationProvider>
                      <ExalusCreateActionContextProvider>
                        <LavvaCreateActionContextProvider>
                          {children}
                          <OverAppLocal />
                        </LavvaCreateActionContextProvider>
                      </ExalusCreateActionContextProvider>
                    </CreateInstallationProvider>
                  </NativeProvider>
                </NativeFunctionsProvider>
              </InstallationProvider>
            </ExalusContextProvider>
          </ExalusDevicesContextProvider>
        </ExalusStorageContextProvider>
      </ExalusServicesContextProvider>
    </ApolloProviderWrapper>
  </CustomSnackbarProvider>
);

const Providers: React.FC<ProvidersProps> = ({ children }) => (
  <CustomSnackbarProvider>
    <SnackbarUtilsConfigurator />
    <GraphqlProvider>
      <ApolloProviderWrapper>
        <DevicesAndChannelsProvider>
          <ExalusServicesContextProvider>
            <ExalusStorageContextProvider>
              <ExalusDevicesContextProvider>
                <ExalusContextProvider>
                  <InstallationProvider>
                    <NativeFunctionsProvider>
                      <NativeProvider>
                        <CreateInstallationProvider>
                          <ShareInstallationProvider>
                            <ExalusCreateActionContextProvider>
                              <LavvaCreateActionContextProvider>
                                {children}
                                <OverApp />
                              </LavvaCreateActionContextProvider>
                            </ExalusCreateActionContextProvider>
                          </ShareInstallationProvider>
                        </CreateInstallationProvider>
                      </NativeProvider>
                    </NativeFunctionsProvider>
                  </InstallationProvider>
                </ExalusContextProvider>
              </ExalusDevicesContextProvider>
            </ExalusStorageContextProvider>
          </ExalusServicesContextProvider>
        </DevicesAndChannelsProvider>
      </ApolloProviderWrapper>
    </GraphqlProvider>
  </CustomSnackbarProvider>
);

const GlobalProviders: React.FC<ProvidersProps> = ({ children }) => {
  return !isLocalApp ? <Providers>{children}</Providers> : <ProvidersLocal>{children}</ProvidersLocal>;
};

export default GlobalProviders;
